import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../config";
import { isPlatformDirector } from "../../utils/roles";
import { CHECKOUT_TEXT_DEFAULTS as DEFAULTS } from "../../hooks/useCheckoutTexts";
import { mergeCheckoutTextsWithDefaults } from "../../utils/mergeCheckoutTexts";
import "./TextoCarrito.css";

const TextoCarrito = () => {
  const [rol, setRol] = useState("");
  const isDirector = isPlatformDirector(rol);

  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("success");

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && data?.usuario?.rol) setRol(data.usuario.rol);
      })
      .catch(() => {});
  }, []);

  const loadTexts = useCallback(() => {
    fetch(`${API_URL}/api/configuracion/texto-carrito`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("load failed");
        return res.json();
      })
      .then((data) => {
        setForm(
          mergeCheckoutTextsWithDefaults(data, DEFAULTS, {
            treatEmptyArraysAsMissing: false,
          }),
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadTexts();
  }, [loadTexts]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCaracteristicaChange = (index, value) => {
    setForm((prev) => {
      const updated = [...prev.caracteristicas];
      updated[index] = value;
      return { ...prev, caracteristicas: updated };
    });
  };

  const addCaracteristica = () => {
    setForm((prev) => ({
      ...prev,
      caracteristicas: [...prev.caracteristicas, ""],
    }));
  };

  const removeCaracteristica = (index) => {
    setForm((prev) => ({
      ...prev,
      caracteristicas: prev.caracteristicas.filter((_, i) => i !== index),
    }));
  };

  const handleBeneficioChange = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.beneficios];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, beneficios: updated };
    });
  };

  const addBeneficio = () => {
    setForm((prev) => ({
      ...prev,
      beneficios: [...prev.beneficios, { titulo: "", descripcion: "" }],
    }));
  };

  const removeBeneficio = (index) => {
    setForm((prev) => ({
      ...prev,
      beneficios: prev.beneficios.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!isDirector) return;
    setSaving(true);
    setMsg("");

    try {
      const res = await fetch(`${API_URL}/api/configuracion/texto-carrito`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Error al guardar");
      }

      const saved = await res.json();
      setForm(
        mergeCheckoutTextsWithDefaults(saved, DEFAULTS, {
          treatEmptyArraysAsMissing: false,
        }),
      );
      setMsg("Textos del carrito actualizados correctamente.");
      setMsgType("success");
    } catch (err) {
      setMsg(err.message || "Error al guardar los cambios.");
      setMsgType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = () => {
    setForm(DEFAULTS);
    setMsg("Valores por defecto restaurados. Pulsa «Guardar» para aplicarlos.");
    setMsgType("info");
  };

  return (
    <section className="cfgx-section">
      <h2>Texto del carrito y checkout</h2>
      <p className="cfgx-help">
        Configura los textos que se muestran en la página de carrito/checkout.
      </p>

      <div className="tc-form">
        <div className="tc-field">
          <label htmlFor="tc-titulo">Título de la página</label>
          <input
            id="tc-titulo"
            type="text"
            value={form.tituloPagina}
            onChange={(e) => handleFieldChange("tituloPagina", e.target.value)}
            disabled={!isDirector}
          />
        </div>

        <div className="tc-field">
          <label htmlFor="tc-subtitulo">Subtítulo de la página</label>
          <textarea
            id="tc-subtitulo"
            rows="2"
            value={form.subtituloPagina}
            onChange={(e) =>
              handleFieldChange("subtituloPagina", e.target.value)
            }
            disabled={!isDirector}
          />
        </div>

        <div className="tc-field">
          <label>Características del curso (etiquetas)</label>
          {form.caracteristicas.map((feat, i) => (
            <div key={i} className="tc-array-row">
              <input
                type="text"
                value={feat}
                onChange={(e) => handleCaracteristicaChange(i, e.target.value)}
                disabled={!isDirector}
                placeholder="Ej: Acceso inmediato"
              />
              {isDirector && form.caracteristicas.length > 1 && (
                <button
                  type="button"
                  className="tc-btn-remove"
                  onClick={() => removeCaracteristica(i)}
                  aria-label="Eliminar característica"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {isDirector && (
            <button
              type="button"
              className="tc-btn-add"
              onClick={addCaracteristica}
            >
              + Añadir característica
            </button>
          )}
        </div>

        <div className="tc-field">
          <label>Beneficios (barra lateral)</label>
          {form.beneficios.map((b, i) => (
            <div key={i} className="tc-beneficio-row">
              <input
                type="text"
                value={b.titulo}
                onChange={(e) =>
                  handleBeneficioChange(i, "titulo", e.target.value)
                }
                disabled={!isDirector}
                placeholder="Título del beneficio"
              />
              <textarea
                rows="2"
                value={b.descripcion}
                onChange={(e) =>
                  handleBeneficioChange(i, "descripcion", e.target.value)
                }
                disabled={!isDirector}
                placeholder="Descripción del beneficio"
              />
              {isDirector && form.beneficios.length > 1 && (
                <button
                  type="button"
                  className="tc-btn-remove"
                  onClick={() => removeBeneficio(i)}
                  aria-label="Eliminar beneficio"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {isDirector && (
            <button type="button" className="tc-btn-add" onClick={addBeneficio}>
              + Añadir beneficio
            </button>
          )}
        </div>

        <div className="tc-field">
          <label htmlFor="tc-seguridad">Texto de seguridad</label>
          <input
            id="tc-seguridad"
            type="text"
            value={form.textoSeguridad}
            onChange={(e) =>
              handleFieldChange("textoSeguridad", e.target.value)
            }
            disabled={!isDirector}
          />
        </div>

        <div className="tc-field">
          <label htmlFor="tc-vacio-titulo">
            Título del carrito vacío
          </label>
          <input
            id="tc-vacio-titulo"
            type="text"
            value={form.tituloCarritoVacio}
            onChange={(e) =>
              handleFieldChange("tituloCarritoVacio", e.target.value)
            }
            disabled={!isDirector}
          />
        </div>

        <div className="tc-field">
          <label htmlFor="tc-vacio-desc">
            Descripción del carrito vacío
          </label>
          <textarea
            id="tc-vacio-desc"
            rows="3"
            value={form.descripcionCarritoVacio}
            onChange={(e) =>
              handleFieldChange("descripcionCarritoVacio", e.target.value)
            }
            disabled={!isDirector}
          />
        </div>

        {isDirector && (
          <div className="tc-actions">
            <button
              className="cfgx-btn-guardar"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
            <button
              className="cfgx-btn-quitar"
              onClick={handleRestoreDefaults}
              disabled={saving}
            >
              Restaurar valores por defecto
            </button>
          </div>
        )}

        {msg && (
          <p className={`tc-msg tc-msg--${msgType}`}>{msg}</p>
        )}
      </div>
    </section>
  );
};

export default TextoCarrito;
